import { Component, effect, inject, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { AUTHENTICATION_SERVICE } from '@processpuzzle/auth';
import { SignupStore } from '../domain/signup.store';
import { AdministratorStepComponent } from './steps/administrator-step.component';
import { BillingPartyStepComponent } from './steps/billing-party-step.component';
import { ConsentStepComponent } from './steps/consent-step.component';
import { OrganizationStepComponent } from './steps/organization-step.component';
import { PlanStepComponent } from './steps/plan-step.component';

/**
 * The sign-up funnel: five steps, one submit.
 *
 * **`mat-stepper` is this repository's first use of `@angular/material/stepper`**, so there is no
 * house style to follow here. It is `linear` and the steps are driven by the store's own
 * completeness answers rather than by each step form's validity, because the store is what survives
 * the authentication redirect — a step's form does not exist after the page reloads, so `linear`
 * against form validity alone would put a returning customer back at step one with a full draft.
 *
 * **Authentication happens at submit, not on route entry.** The route is public deliberately: a
 * visitor comparing prices should reach the plans without being bounced to Keycloak, and the
 * contract only requires an authenticated, email-verified account to *submit*. `authenticate()`
 * before the POST is what enforces that.
 *
 * **Which is why the draft is persisted.** With Keycloak, `authenticate()` redirects the whole
 * document away and back, so the five-step form the customer has just filled in would be gone by
 * the time they returned — and they would land on the step that sent them away, without knowing
 * what had been lost. `SignupDraftStorage` holds it in `sessionStorage`; {@link ngOnInit} restores
 * it.
 *
 * **The confirmation is its own route**, `status/:orgKey`, rather than a sixth step. It immediately
 * constructs the deterministic application address from the submitted key, without waiting for
 * provisioning. It is also why the route has to be a child: `navigationItems` reads only top-level
 * `router.config`, so a child route never reaches the sidenav.
 */
@Component({
  selector: 'pp-subscription-funnel',
  imports: [
    MatStepperModule,
    MatButton,
    MatProgressSpinner,
    TranslocoDirective,
    PlanStepComponent,
    OrganizationStepComponent,
    BillingPartyStepComponent,
    AdministratorStepComponent,
    ConsentStepComponent,
  ],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup'">
      <h1>{{ t('title') }}</h1>
      <p class="funnel-lead">{{ t('lead') }}</p>

      <mat-stepper linear #stepper>
        <mat-step [completed]="store.isStepComplete('plan')" [label]="t('plan.label')">
          <pp-plan-step />
          <div class="step-actions">
            <button type="button" mat-raised-button color="primary" matStepperNext [disabled]="!store.isStepComplete('plan')">{{ t('next') }}</button>
          </div>
        </mat-step>

        <mat-step [completed]="store.isStepComplete('organization')" [label]="t('organization.label')">
          <pp-organization-step />
          <div class="step-actions">
            <button type="button" mat-button matStepperPrevious>{{ t('back') }}</button>
            <!-- Blocked on the live key check as well as on the fields. Letting a customer past a
                 key the server has already said is taken would move the refusal to the submit, five
                 steps later, where it costs them the whole form. -->
            <button type="button" mat-raised-button color="primary" matStepperNext [disabled]="!store.isStepComplete('organization') || store.keyRejected()">
              {{ t('next') }}
            </button>
          </div>
        </mat-step>

        <mat-step [completed]="store.isStepComplete('billingParty')" [label]="t('billing_party.label')">
          <pp-billing-party-step />
          <div class="step-actions">
            <button type="button" mat-button matStepperPrevious>{{ t('back') }}</button>
            <button type="button" mat-raised-button color="primary" matStepperNext [disabled]="!store.isStepComplete('billingParty')">{{ t('next') }}</button>
          </div>
        </mat-step>

        <mat-step [completed]="store.isStepComplete('administrator')" [label]="t('administrator.label')">
          <pp-administrator-step />
          <div class="step-actions">
            <button type="button" mat-button matStepperPrevious>{{ t('back') }}</button>
            <button type="button" mat-raised-button color="primary" matStepperNext [disabled]="!store.isStepComplete('administrator')">{{ t('next') }}</button>
          </div>
        </mat-step>

        <mat-step [completed]="store.isStepComplete('consent')" [label]="t('consent.label')">
          <pp-consent-step />
          <div class="step-actions">
            <button type="button" mat-button matStepperPrevious>{{ t('back') }}</button>
            <button id="signup-submit" type="button" mat-raised-button color="primary" test-id="signup-submit" [disabled]="!store.canSubmit()" (click)="submit()">
              @if (store.submitting()) {
                <mat-progress-spinner diameter="18" mode="indeterminate" />
              }
              {{ t('submit') }}
            </button>
          </div>
          <p class="funnel-note">{{ t('submitNote') }}</p>
        </mat-step>
      </mat-stepper>
    </ng-container>
  `,
  styles: [
    `
      .funnel-lead {
        max-width: 60ch;
      }

      .step-actions {
        display: flex;
        gap: 8px;
        margin-top: 16px;
      }

      .funnel-note {
        font-size: 0.85rem;
        opacity: 0.75;
        max-width: 60ch;
      }
    `,
  ],
})
export class SubscriptionFunnelComponent implements OnInit {
  readonly store = inject(SignupStore);
  private readonly authenticationService = inject(AUTHENTICATION_SERVICE);
  private readonly router = inject(Router);

  constructor() {
    // Navigation on success rather than inside the store: the store knows a sign-up was accepted,
    // and where to go next is a routing decision that belongs to the surface.
    effect(() => {
      const submitted = this.store.submitted();
      if (submitted) void this.router.navigate(['/signup', 'status', submitted.orgKey]);
    });
  }

  ngOnInit(): void {
    this.store.restoreDraft();
    this.store.loadPlans();
  }

  /**
   * Authenticates, then submits.
   *
   * `authenticate()` first, because the contract requires an authenticated, email-verified account
   * to submit and because email verification is the minimum defence against minting trial
   * organizations in bulk. With Keycloak this can redirect the whole document — in which case the
   * `submit()` below never runs, the draft has already been persisted, and the customer resumes here
   * on their way back.
   *
   * Failures are not handled here. `centralHttpErrorInterceptor` surfaces the HTTP ones, and an
   * authentication that is refused leaves the funnel exactly as it was, which is the right state
   * to be in.
   */
  async submit(): Promise<void> {
    if (!this.store.canSubmit()) return;
    await this.authenticationService.authenticate();
    this.store.submit();
  }
}
